import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, Calendar } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { getBlogPost, getBlogPosts } from "@/lib/api";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getBlogPost(slug)
      .then((post) => {
        setPost(post);
        return getBlogPosts().then((all) => {
          setRelated(all.filter((p) => p.slug !== slug).slice(0, 3));
        });
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-40 mx-auto max-w-3xl px-6 md:px-12" data-testid="blog-detail-loading">
        <div className="h-8 w-40 rounded-full bg-white/5 animate-pulse" />
        <div className="mt-6 h-16 w-3/4 rounded-2xl bg-white/5 animate-pulse" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-40 mx-auto max-w-3xl px-6 md:px-12 text-center">
        <p className="font-mono text-xs text-zinc-500 uppercase">404</p>
        <h1 className="font-display text-4xl mt-4">Article not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-nexus-cyan">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page" className="pt-32 pb-20">
      <section className="mx-auto max-w-4xl px-6 md:px-12 mesh-bg pb-10">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/blog" className="hover:text-foreground">Blog</Link> · {post.category}
          </p>
          <h1 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
            {post.title}
          </h1>
          <div className="mt-7 flex flex-wrap items-center gap-6 text-sm text-zinc-400">
            <span className="font-medium text-foreground">{post.author}</span>
            <span className="text-zinc-500">·</span>
            <span>{post.author_role}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {new Date(post.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.read_time}</span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 aspect-[16/9] rounded-3xl overflow-hidden border border-white/[0.08]">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-6 md:px-12 mt-12 prose-content">
        <Reveal>
          <div className="font-cabinet text-lg md:text-xl text-zinc-300 leading-[1.8] whitespace-pre-line">
            {post.content}
          </div>
        </Reveal>

        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-white/[0.06] flex flex-wrap gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2 self-center">Tags</span>
            {post.tags.map((t) => (
              <span key={t} className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-zinc-300">
                #{t}
              </span>
            ))}
          </div>
        )}
      </section>

      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 md:px-12 mt-32">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Read next ]</p>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight">More from the workshop.</h2>
          </Reveal>
          <Stagger className="mt-10 grid md:grid-cols-3 gap-5">
            {related.map((p) => (
              <Item key={p.id}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="group block rounded-2xl glass border-white/[0.08] overflow-hidden h-full"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <span className="rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest text-zinc-300 px-3 py-1">
                      {p.category}
                    </span>
                    <h3 className="mt-3 font-display text-xl tracking-tight line-clamp-2 group-hover:text-nexus-cyan transition">
                      {p.title}
                    </h3>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs text-zinc-400">
                      Read <ArrowUpRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              </Item>
            ))}
          </Stagger>
        </section>
      )}

      <section className="mx-auto max-w-5xl px-6 md:px-12 mt-24">
        <div className="rounded-[2rem] glass border-white/[0.08] p-10 md:p-14 text-center">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">
            Have a project in mind?
          </h2>
          <p className="mt-3 text-zinc-400 max-w-lg mx-auto">
            We'd love to hear what you're building. Tell us a bit about it and we'll respond within one business day.
          </p>
          <Link to="/contact" className="mt-7 inline-flex items-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-6 py-3 text-sm">
            Start a project <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
